import { useEffect, useId, useRef, useState } from "react";
import type { SyntheticEvent } from "react";

export interface ConfirmDialogProps {
  title: string;
  description: string;
  confirmationText?: string;
  confirmLabel: string;
  destructive?: boolean;
  confirmDisabled?: boolean;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  description,
  confirmationText,
  confirmLabel,
  destructive = false,
  confirmDisabled = false,
  isPending,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const confirmationId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const confirmationRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [confirmation, setConfirmation] = useState("");
  const confirmed =
    confirmationText === undefined || confirmation === confirmationText;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const activeElement = document.activeElement;
    const opener = activeElement instanceof HTMLElement ? activeElement : null;

    if (typeof dialog.showModal === "function") {
      try {
        if (!dialog.open) dialog.showModal();
      } catch {
        dialog.setAttribute("open", "");
      }
    } else {
      dialog.setAttribute("open", "");
    }

    const focusTarget = confirmationRef.current ?? cancelRef.current;
    focusTarget?.focus();

    return () => {
      if (dialog.open && typeof dialog.close === "function") {
        try {
          dialog.close();
        } catch {
          dialog.removeAttribute("open");
        }
      } else {
        dialog.removeAttribute("open");
      }
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  function handleNativeCancel(event: SyntheticEvent<HTMLDialogElement>) {
    event.preventDefault();
    if (!isPending) onCancel();
  }

  return (
    <dialog
      ref={dialogRef}
      className="panel fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-lg p-5 backdrop:bg-black/35"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onCancel={handleNativeCancel}
    >
      <h2 id={titleId} className="text-lg font-semibold">
        {title}
      </h2>
      <p id={descriptionId} className="mt-2 text-sm text-muted-foreground">
        {description}
      </p>

      {confirmationText !== undefined ? (
        <label
          className="mt-4 block text-sm font-medium"
          htmlFor={confirmationId}
        >
          <span className="mb-1.5 block">
            Введите «{confirmationText}» для подтверждения
          </span>
          <input
            ref={confirmationRef}
            id={confirmationId}
            className="control"
            value={confirmation}
            disabled={isPending}
            autoComplete="off"
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </label>
      ) : null}

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          ref={cancelRef}
          className="inline-flex h-10 items-center justify-center rounded-md border bg-card px-4 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          disabled={isPending}
          onClick={onCancel}
        >
          Отмена
        </button>
        <button
          className={`inline-flex h-10 items-center justify-center rounded-md px-4 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
            destructive
              ? "bg-danger text-destructive-foreground"
              : "bg-primary text-primary-foreground"
          }`}
          type="button"
          aria-busy={isPending}
          disabled={isPending || confirmDisabled || !confirmed}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
